import { isNil } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { useGenericUploadActions } from "@ryde/redux";
import {
  appLanguageSelector,
  castErrorResult,
  createErrorResultFromException,
  createErrorResultWithNoErrorDetails,
  createSuccessResult,
  errors,
  IGenericUploadEntityCreateFieldDto,
  IGenericUploadPrepResult,
  Log,
  RydeUiUnit,
  SimpleResult,
} from "@ryde/shared";

import { IGenericUploadPrepareStatistics } from "../generic-upload.models";
import { genericUploadParseFile } from "../helpers";

export const useGenericUploadPreparingFile = (entity: RydeUiUnit) => {
  const { t } = useTranslation();
  const appLanguage = useRecoilValue(appLanguageSelector);

  const [isPreparing, setIsPreparing] = useState(false);
  const [uploadStatistics, setUploadStatistics] =
    useState<IGenericUploadPrepareStatistics>({
      totalRows: 0,
      rowsToCreate: 0,
      rowsToUpdate: 0,
    });
  const [uploadResult, setUploadResult] = useState<
    IGenericUploadPrepResult | undefined
  >(undefined);

  const { genericUploadPrepare } = useGenericUploadActions(entity);

  const prepareFile = useEvent(
    async (
      file: File | undefined,
      entityCreateFields: IGenericUploadEntityCreateFieldDto[],
    ): Promise<SimpleResult<IGenericUploadPrepResult>> => {
      if (!file) {
        Log.warnAndToast(t(errors.common.fileIsNotSelected));
        return createErrorResultWithNoErrorDetails("File is not selected");
      }

      setIsPreparing(true);

      try {
        const parseResult = await genericUploadParseFile({ file });

        const prepResult = await genericUploadPrepare({
          language: appLanguage,
          entity,
          data: parseResult.data,
          entityCreateFields,
        });

        if (!prepResult.success || !prepResult.data) {
          return castErrorResult(prepResult);
        }

        const prepResultData = prepResult.data;
        const stats: IGenericUploadPrepareStatistics = {
          totalRows: 0,
          rowsToCreate: 0,
          rowsToUpdate: 0,
        };

        for (const row of prepResultData.data) {
          stats.totalRows += 1;

          if (isNil(row.id)) {
            stats.rowsToCreate += 1;
          } else {
            stats.rowsToUpdate += 1;
          }
        }

        setUploadResult(prepResultData);
        setUploadStatistics({ ...stats });

        return createSuccessResult(prepResultData);
      } catch (error) {
        Log.errorAndToast(t(errors.common.unknownError), error);
        return createErrorResultFromException(error, "Failed to process file");
      } finally {
        setIsPreparing(false);
      }
    },
  );

  return {
    isPreparing,
    uploadStatistics,
    uploadResult,
    prepareFile,
  };
};
